import { action, computed, observable } from 'mobx'
import { switchMap } from 'rxjs'
import type { QuestionListViewModelFactory } from '@/features/jobs'
import type { QuestionEditorViewModel } from '@/features/jobs/questions/QuestionEditorViewModel'
import type {
  AnswersMap,
  DocumentsQuestion,
  Question,
} from '@/features/jobs/questions/questions'
import type { QuestionListViewModel } from '@/features/jobs/questions/QuestionListViewModel'
import type { ActivationLifecycle } from '@/infra/activation'
import type { Id } from '@/infra/id'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { ObjectStorage } from '@/infra/object-storage'
import { tapAction, toStream } from '@/utils/mobx-util'
import {
  parseStoredResponses,
  type PreviewResponse,
  toEngineAnswer,
  withEngineAnswer,
} from './preview-questions'
import { StoredValue } from './StoredValue'

/**
 * A question of a preview input group, in the order the questions are asked.
 * Documents questions need a job to be answered, so they only list the
 * documents they ask for.
 */
export type PreviewInputRow =
  | { type: 'Answerable'; questionVm: QuestionEditorViewModel }
  | { type: 'Documents'; question: DocumentsQuestion }

/**
 * A set of preview inputs, answered with the job intake's question editors.
 * The responses are kept in local storage per legend, so a preview survives
 * reloads.
 */
export class PreviewInputGroupViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  @observable.ref private accessor _questions: Question[] = []

  /**
   * The question editors.
   */
  @observable.ref private accessor _listVm: QuestionListViewModel

  /**
   * The responses, including those to questions that aren't asked right
   * now, so they come back when the question is asked again.
   */
  @observable.ref accessor responses: PreviewResponse[] = []

  private readonly _answers$ = toStream(() => this._listVm, true).pipe(
    switchMap((listVm) => listVm.answersChanged$),
    tapAction((answers: AnswersMap) => this._receiveAnswers(answers)),
  )

  constructor(
    getStorage: () => ObjectStorage,
    private readonly _getLegendId: () => Id | null,
    private readonly _storageKeyPrefix: string,
    private readonly _createQuestionListViewModel: QuestionListViewModelFactory,
  ) {
    this._listVm = _createQuestionListViewModel()
    this._lifecycle.manage(this._listVm)
    this._lifecycle.manage(
      new StoredValue({
        getStorage,
        getKey: () => this._storageKey,
        getValue: () => this.responses,
        load: (stored) => {
          this.responses = parseStoredResponses(stored)
          this._listVm.reconcile(this._withResponses(this._answerableQuestions))
        },
      }),
    )
    this._lifecycle.manage(this._answers$)
  }

  /**
   * Whether any question is answered.
   */
  @computed
  get hasResponses() {
    return this.responses.length > 0
  }

  @computed
  get rows(): PreviewInputRow[] {
    return this._questions.flatMap((question): PreviewInputRow[] => {
      if (question.type === 'Documents') {
        return [{ type: 'Documents', question }]
      }
      const questionVm = this._listVm.questionVms.find(
        (vm) => vm.id === question.id,
      )
      return questionVm ? [{ type: 'Answerable', questionVm }] : []
    })
  }

  @computed
  private get _answerableQuestions() {
    return this._questions.filter((q) => q.type !== 'Documents')
  }

  @computed
  private get _storageKey() {
    const legendId = this._getLegendId()
    return legendId ? `${this._storageKeyPrefix}.${legendId}` : null
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Shows the questions, with their responses.
   *
   * @param questions
   */
  @action.bound
  sync(questions: Question[]) {
    this._questions = questions
    this._listVm.reconcile(this._withResponses(this._answerableQuestions))
  }

  /**
   * Clears all responses. The question list is replaced, since it remembers
   * answers to questions that aren't asked right now.
   */
  @action.bound
  clear() {
    this.responses = []
    this._lifecycle.remove(this._listVm)
    this._listVm = this._createQuestionListViewModel()
    this._lifecycle.manage(this._listVm)
    this._listVm.reconcile(this._withResponses(this._answerableQuestions))
  }

  /**
   * Keeps the responses to questions that aren't asked right now.
   *
   * @param answers
   */
  @action.bound
  private _receiveAnswers(answers: AnswersMap) {
    const asked = new Set(this._listVm.questionVms.map((vm) => vm.id))
    const kept = this.responses.filter(
      (response) => !asked.has(response.id) && !answers.has(response.id),
    )
    const answered = [...answers].flatMap(([id, answer]) => {
      const engineAnswer = toEngineAnswer(answer)
      return engineAnswer === undefined ? [] : [{ id, answer: engineAnswer }]
    })
    this.responses = [...kept, ...answered]
  }

  private _withResponses(questions: Question[]) {
    return questions.map((question) =>
      withEngineAnswer(
        question,
        this.responses.find((r) => r.id === question.id)?.answer,
      ),
    )
  }
}
